import React, { useState, useEffect } from 'react';
import './PhaseTimer.scss';

interface PhaseTimerProps {
  event: any;
}

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

const PhaseTimer: React.FC<PhaseTimerProps> = ({ event }) => {
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    if (!event?.setting?.show_phase_timer || !event?.setting?.next_phase_ends) {
      return;
    }

    const calculateTimeLeft = (): TimeLeft => {
      const endTime = new Date(event.setting.next_phase_ends).getTime();
      const now = new Date().getTime();
      const difference = endTime - now;

      if (difference <= 0) {
        setIsExpired(true);
        return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      }

      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      return { days, hours, minutes, seconds };
    };

    // Initial calculation
    setTimeLeft(calculateTimeLeft());

    // Update every second
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, [event?.setting?.next_phase_ends]);

  // Don't render if phase timer is not enabled or no end time
  if (!event?.setting?.show_phase_timer || !event?.setting?.next_phase_ends) {
    return null;
  }

  const formatTime = (value: number): string => {
    return value < 10 ? `0${value}` : value.toString();
  };

  const timerLabel = event.setting.phase_timer_label || "Current phase ends in:";

  return (
    <div className="phase-timer-container">
      <div className="phase-timer">
        <div className="phase-timer-label">{timerLabel}</div>
        <div className="phase-timer-countdown">
          <div className="timer-unit">
            <span className="timer-value" id="phase-days">
              {formatTime(timeLeft.days)}
            </span>
            <span className="timer-label">Days</span>
          </div>
          <div className="timer-unit">
            <span className="timer-value" id="phase-hours">
              {formatTime(timeLeft.hours)}
            </span>
            <span className="timer-label">Hours</span>
          </div>
          <div className="timer-unit">
            <span className="timer-value" id="phase-minutes">
              {formatTime(timeLeft.minutes)}
            </span>
            <span className="timer-label">Minutes</span>
          </div>
          <div className="timer-unit">
            <span className="timer-value" id="phase-seconds">
              {formatTime(timeLeft.seconds)}
            </span>
            <span className="timer-label">Seconds</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhaseTimer;
